import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #overlayMutationObserver = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #evaluationFrame = null;

  /** @type {number | null} */
  #hideAfterBottomTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #buyButtonsAreAbove = false;

  /** @type {boolean} */
  #lockedAfterBottomHide = false;

  /** @type {boolean} */
  #suppressUntilNextScroll = false;

  /** @type {number} */
  #suppressMinimumUntil = 0;

  /** @type {number} */
  #lastScrollY = this.#getScrollY();

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupOverlayObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal, capture: true });

    window.addEventListener('scroll', this.#handleWindowScroll, { signal, passive: true });
    window.addEventListener('resize', this.#handleWindowResize, { signal });

    this.#getInitialQuantity();
    this.#requestStickyEvaluation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#overlayMutationObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#evaluationFrame) {
      window.cancelAnimationFrame(this.#evaluationFrame);
    }

    this.#clearDelayedBottomHide();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer');
    if (!footer) return;

    this.#buyButtonsBlock = /** @type {HTMLElement} */ (buyButtonsBlock);

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const rect = entry.boundingClientRect;

      this.#buyButtonsAreAbove = !entry.isIntersecting && rect.bottom < -24;
      this.#requestStickyEvaluation();
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#hiddenByBottom = entry.isIntersecting;
        this.#requestStickyEvaluation();
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Watches localization popup state.
   */
  #setupOverlayObserver() {
    if (this.#overlayMutationObserver) return;

    this.#overlayMutationObserver = new MutationObserver(() => {
      if (this.#isBlockingOverlayOpen()) {
        this.#suppressUntilNextScroll = true;
        this.#suppressMinimumUntil = Date.now() + 400;
        this.#clearDelayedBottomHide();
        this.#hideStickyBar();
        return;
      }

      this.#requestStickyEvaluation();
    });

    // Only the localization popups block the bar, so only their open state is watched (this used to
    // observe every DOM change and class/hidden/aria-hidden change of the whole page). The popups are
    // rendered with the page; observation follows them if a script moves them.
    document.querySelectorAll('[data-eg-loc-popup], .eg-loc-popup').forEach((popup) => {
      this.#overlayMutationObserver?.observe(popup, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'open', 'aria-hidden'],
      });
    });
  }

  /**
   * Handles the add to cart button click in the sticky bar.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events.
   *
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
    this.#requestStickyEvaluation();
  };

  /**
   * Handles variant selected events.
   *
   * @param {CustomEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete.
   *
   * @param {CustomEvent} _event - The cart event.
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events.
   *
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event.
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Handles real window scroll.
   */
  #handleWindowScroll = () => {
    const scrollY = this.#getScrollY();

    if (Math.abs(scrollY - this.#lastScrollY) > 1 && Date.now() > this.#suppressMinimumUntil) {
      this.#suppressUntilNextScroll = false;
    }

    this.#lastScrollY = scrollY;
    this.#requestStickyEvaluation();
  };

  /**
   * Handles viewport resize/orientation changes.
   */
  #handleWindowResize = () => {
    this.#requestStickyEvaluation();
  };

  /**
   * Suppresses sticky activation caused by localization triggers and product accordions.
   *
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-eg-open-loc-popup], .eg-loc-mobile-trigger')) {
      this.#suppressUntilNextScroll = true;
      this.#suppressMinimumUntil = Date.now() + 400;
      this.#clearDelayedBottomHide();
      this.#hideStickyBar();
      return;
    }

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement || !sectionElement.contains(target)) return;

    const accordionSummary = target.closest('accordion-custom summary, .accordion summary, details summary');
    if (!accordionSummary) return;

    if (!this.#isStuck) {
      this.#suppressUntilNextScroll = true;
      this.#suppressMinimumUntil = Date.now() + 900;
      this.#clearDelayedBottomHide();
      this.#hideStickyBar();
    }
  };

  /**
   * Requests a debounced sticky state evaluation.
   */
  #requestStickyEvaluation() {
    if (this.#evaluationFrame) return;

    this.#evaluationFrame = window.requestAnimationFrame(() => {
      this.#evaluationFrame = null;
      this.#evaluateStickyBar();
    });
  }

  /**
   * Final source of truth for sticky visibility.
   */
  #evaluateStickyBar() {
    if (!this.#buyButtonsBlock) return;

    if (this.dataset.variantAvailable && this.dataset.variantAvailable !== 'true') {
      this.#lockedAfterBottomHide = false;
      this.#clearDelayedBottomHide();
      this.#hideStickyBar();
      return;
    }

    if (this.#suppressUntilNextScroll || this.#isBlockingOverlayOpen()) {
      this.#clearDelayedBottomHide();
      this.#hideStickyBar();
      return;
    }

    /*
      Important:
      If the sticky bar was hidden after reaching the bottom/footer zone,
      do not allow it to flash again while the user scrolls upward from the bottom.
      It unlocks only after the main Add to cart block returns to the viewport area.
    */
    if (!this.#buyButtonsAreAbove) {
      this.#lockedAfterBottomHide = false;
      this.#clearDelayedBottomHide();
      this.#hideStickyBar();
      return;
    }

    if (this.#hiddenByBottom) {
      if (this.#isStuck) {
        this.#scheduleDelayedBottomHide();
      } else {
        this.#hideStickyBar();
      }

      return;
    }

    if (this.#lockedAfterBottomHide) {
      this.#clearDelayedBottomHide();
      this.#hideStickyBar();
      return;
    }

    this.#clearDelayedBottomHide();
    this.#showStickyBar();
  }

  /**
   * Delays hiding only when the sticky bar reaches the bottom/footer hide zone.
   */
  #scheduleDelayedBottomHide() {
    if (this.#hideAfterBottomTimeout) return;

    this.#hideAfterBottomTimeout = window.setTimeout(() => {
      this.#hideAfterBottomTimeout = null;

      if (
        this.#suppressUntilNextScroll ||
        this.#isBlockingOverlayOpen()
      ) {
        this.#hideStickyBar();
        return;
      }

      if (!this.#hiddenByBottom) {
        this.#requestStickyEvaluation();
        return;
      }

      this.#lockedAfterBottomHide = true;
      this.#hideStickyBar();
    }, 5000);
  }

  /**
   * Clears delayed bottom-hide timer.
   */
  #clearDelayedBottomHide() {
    if (!this.#hideAfterBottomTimeout) return;

    window.clearTimeout(this.#hideAfterBottomTimeout);
    this.#hideAfterBottomTimeout = null;
  }

  /**
   * Checks whether localization popup is currently open.
   */
  #isBlockingOverlayOpen() {
    if (document.querySelector('[data-eg-loc-popup].is-open:not([hidden]), .eg-loc-popup.is-open:not([hidden])')) {
      return true;
    }

    return false;
  }

  /**
   * Gets current page scroll Y.
   */
  #getScrollY() {
    return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  /**
   * Shows the sticky bar with animation.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Gets the product form element.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute.
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity.
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}